// engine/suspicion.ts — the strike / suspicion meter (SPEC §7).
// 3 strikes anywhere → FLAGGED FOR REVIEW. Suspicion *events* animate but never strike.
// Strike sources are ONLY those named in SPEC §3; levels decide, this just accounts.

export const MAX_STRIKES = 3;

export interface StrikeRecord {
  /** level id that filed it, e.g. "L06" */
  level: string;
  /** short reason, shown police-report style on the FLAGGED screen */
  reason: string;
}

type Listener = (state: SuspicionState) => void;

export interface SuspicionState {
  strikes: number;
  records: readonly StrikeRecord[];
  flagged: boolean;
}

export class SuspicionMeter {
  private records: StrikeRecord[] = [];
  private listeners = new Set<Listener>();
  /** pulses when a suspicion event fires (visual only) */
  private eventListeners = new Set<() => void>();

  get strikes(): number {
    return this.records.length;
  }

  get flagged(): boolean {
    return this.records.length >= MAX_STRIKES;
  }

  get state(): SuspicionState {
    return {
      strikes: this.strikes,
      records: this.records.slice(),
      flagged: this.flagged,
    };
  }

  /** File a strike. Returns true if this strike tipped into FLAGGED. */
  strike(level: string, reason: string): boolean {
    this.records.push({ level, reason });
    this.emit();
    return this.flagged;
  }

  /** A too-perfect / anomalous event: pulse the meter, no strike (§7). */
  suspicionEvent(): void {
    for (const l of this.eventListeners) l();
  }

  /** True when the next strike would file the subject — drives the §7 warning line. */
  get atWarningThreshold(): boolean {
    return this.strikes === MAX_STRIKES - 1;
  }

  /** Clear strikes on APPEAL (§4). Traits survive; that's held elsewhere. */
  clearStrikes(): void {
    this.records = [];
    this.emit();
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  onEvent(fn: () => void): () => void {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }

  private emit(): void {
    const s = this.state;
    for (const l of this.listeners) l(s);
  }
}
